/**
 * Sitewide rule: Entry requirements (and matching FAQ answers) that pack
 * several "one of the following" criteria into a run-on paragraph must render
 * as an intro line, markdown bullets, then any closing advisor paragraph.
 *
 * Only splits wording; drops joining or/and, semicolons and trailing
 * punctuation as needed, and capitalises each bullet. Leaves single-criterion
 * copy and already-bulleted lists unchanged.
 */

const FOLLOWING_MARKER = /meet one of the following:/i;

function capitaliseBullet(text: string): string {
  if (!text) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function cleanCriterion(raw: string): string {
  let text = raw.replace(/\s+/g, " ").trim();
  text = text.replace(/^(?:or|and)\s+/i, "");
  text = text.replace(/[.;]+\s*$/g, "");
  return capitaliseBullet(text);
}

function splitTrailingClosing(segment: string): {
  criterion: string;
  closing: string;
} {
  const trimmed = segment.replace(/\s+/g, " ").trim();
  const closingMatch = trimmed.match(
    /^(.*?\S)\.\s+((?:Not sure|If you|New to|Already|Hold|Unsure|Speak|Contact|Email|Call).+)$/i,
  );
  if (closingMatch) {
    return {
      criterion: cleanCriterion(closingMatch[1]),
      closing: closingMatch[2].trim(),
    };
  }

  return { criterion: cleanCriterion(trimmed), closing: "" };
}

function splitSemicolonCriteria(body: string): {
  criteria: string[];
  closing: string;
} {
  const segments = body
    .split(";")
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  if (segments.length < 2) {
    return { criteria: [], closing: "" };
  }

  const criteria: string[] = [];
  let closing = "";

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const isLast = index === segments.length - 1;

    if (!isLast) {
      criteria.push(cleanCriterion(segment));
      continue;
    }

    const split = splitTrailingClosing(segment);
    criteria.push(split.criterion);
    closing = split.closing;
  }

  return { criteria, closing };
}

function toBulletedBlock(
  intro: string,
  criteria: string[],
  closing: string,
): string {
  const bullets = criteria.map((item) => `- ${item}`).join("\n");
  const parts = [intro, "", bullets];
  if (closing) {
    parts.push("", closing);
  }
  return parts.join("\n");
}

function formatFollowingParagraph(paragraph: string): string | null {
  if (/\n\s*-\s+\S/.test(paragraph)) {
    return null;
  }

  const match = paragraph.match(FOLLOWING_MARKER);
  if (!match || match.index == null) {
    return null;
  }

  const introEnd = match.index + match[0].length;
  const intro = paragraph.slice(0, introEnd).trim();
  const remainder = paragraph.slice(introEnd).trim();
  if (!remainder || !remainder.includes(";")) {
    return null;
  }

  const { criteria, closing } = splitSemicolonCriteria(remainder);
  if (criteria.length < 2) {
    return null;
  }

  return toBulletedBlock(intro, criteria, closing);
}

/**
 * Comma-joined FAQ variant used alongside entry copy:
 * "You can enrol if you are A, hold B, or have C. Closing."
 * A may itself contain commas (NMC, GMC, GDC or GPhC).
 *
 * Keeps the lead-in through "You can enrol if", then bullets the three
 * criteria without adding new phrases.
 */
function formatEnrolIfYouAreHoldHave(paragraph: string): string | null {
  if (/\n\s*-\s+\S/.test(paragraph)) {
    return null;
  }

  const match = paragraph.match(
    /^(.*?\bYou can enrol if) you are\s+(.+?),\s+hold\s+(.+?),\s+or have\s+(.+)$/i,
  );
  if (!match) {
    return null;
  }

  const intro = `${match[1].trim()}:`;
  const haveSplit = splitTrailingClosing(match[4]);
  const haveBody = haveSplit.criterion.replace(/^Have\s+/i, "");
  const criteria = [
    cleanCriterion(`you are ${match[2]}`),
    cleanCriterion(`hold ${match[3]}`),
    cleanCriterion(`have ${haveBody.charAt(0).toLowerCase()}${haveBody.slice(1)}`),
  ];

  return toBulletedBlock(intro, criteria, haveSplit.closing);
}

/**
 * Format entry-requirement style markdown. Safe to run on any accordion body:
 * returns the original string when the packed-list pattern is absent.
 */
export function formatEntryRequirementsContent(content: unknown): unknown {
  if (typeof content !== "string") {
    return content;
  }

  const source = content.replace(/\u00a0/g, " ");
  if (!source.trim()) {
    return content;
  }

  // Already a markdown list after a "following:" intro: leave alone.
  if (FOLLOWING_MARKER.test(source)) {
    const after = source.split(FOLLOWING_MARKER)[1] || "";
    if (/^\s*\n?\s*-\s+\S/m.test(after)) {
      return content;
    }
  }

  const paragraphs = source.split(/\n\s*\n/);
  let changed = false;
  const next = paragraphs.map((paragraph) => {
    const compact = paragraph.trim();
    if (!compact) {
      return paragraph;
    }

    const following = formatFollowingParagraph(compact);
    if (following) {
      changed = true;
      return following;
    }

    const enrol = formatEnrolIfYouAreHoldHave(compact);
    if (enrol) {
      changed = true;
      return enrol;
    }

    return paragraph;
  });

  if (!changed) {
    return content;
  }

  return `${next.join("\n\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
}

export function isEntryRequirementsTitle(title?: string | null): boolean {
  if (!title) {
    return false;
  }
  return /^entry\s+requirements?$/i.test(title.trim());
}

/**
 * Whether accordion/FAQ body should be passed through the entry-requirements
 * list formatter (entry titles, or copy that already matches the packed pattern).
 */
export function shouldFormatEntryRequirementsList(
  title?: string | null,
  content?: unknown,
): boolean {
  if (isEntryRequirementsTitle(title)) {
    return true;
  }
  if (typeof content !== "string") {
    return false;
  }
  const text = content.replace(/\u00a0/g, " ");
  return (
    FOLLOWING_MARKER.test(text) ||
    /\bYou can enrol if you are\s+.+\bhold\b.+\bor have\b/i.test(text)
  );
}
